/**
 * ========================================
 * CRM Store - Zustand
 * ========================================
 * Estado global de la aplicación con caché
 *
 * Estrategia:
 * - Auth y UI preferences persisten en localStorage
 * - Chats, mensajes y prospectos NO persisten (siempre fresh)
 * - TTL por tipo de dato (30s, 60s, 5min)
 * - Optimistic updates para mejor UX
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const FROZEN_TTL_MS = 3 * 60 * 1000 // 3 min — TTL del frozen mode (grilla TodoList)

// Calcula el proximo 00:00 en zona Chile como timestamp UTC ms.
// Usado por fullDayMode (opt-in modo dia completo del frozen mode).
function computeNextMidnightChile() {
  const now = new Date()
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  })
  const parts = Object.fromEntries(fmt.formatToParts(now).map(p => [p.type, p.value]))
  const hh = parseInt(parts.hour, 10) || 0
  const mm = parseInt(parts.minute, 10) || 0
  const ss = parseInt(parts.second, 10) || 0
  const secondsRemaining = (24 - hh) * 3600 - mm * 60 - ss
  // Failsafe: si por alguna razon es <=0 (medianoche exacta), agregar 1 dia
  const safe = secondsRemaining > 0 ? secondsRemaining : 24 * 3600
  return Date.now() + safe * 1000
}

const freshFrozenState = () => ({
  active: false,
  snapshot: [],
  deadline: null,
  crossAgentDots: {},
  newRowsHighlight: {},
  zombies: {},
  fullDayMode: false, // opt-in del agente: true = deadline hasta 00:00 Chile en vez de +3min
})

const useCRMStore = create(
  persist(
    (set, get) => ({
      // ========================================
      // AUTH
      // ========================================
      user: null,
      token: null,
      sessionExpired: false, // Flag global para indicar sesión expirada

      setAuth: (user, _token) => {
        // [F2 2026-07-02] La sesion viaja en cookie httpOnly: el token YA NO se guarda
        // en JS/localStorage/Zustand (fragil: caso Ivan storage no-durable + pestanas
        // zombis re-sembrando estado). Solo se recuerda el user para la UI.
        set({ user, token: null, sessionExpired: false })
        if (user) {
          localStorage.setItem('crm_user', JSON.stringify(user))
        }
      },

      // Marcar sesión como expirada (sin hacer logout completo aún)
      setSessionExpired: () => {
        // [fix 2026-07-01] Limpiar tambien token/user en memoria: si no, Zustand re-persiste el
        // token vencido en crm-web-storage y "resucita" al re-loguear en la misma pestana -> loop de deslogeo.
        // (El modal sigue mostrandose porque depende del flag sessionExpired, no del token.)
        set({ sessionExpired: true, token: null, user: null })
      },

      logout: () => {
        set({
          user: null,
          token: null,
          sessionExpired: false,
          chats: [],
          messages: {},
          prospects: {},
          selectedChatPhone: null,
          focusedContactPhone: null
        })
        localStorage.removeItem('crm_token')
        localStorage.removeItem('crm_user')
      },

      // ========================================
      // CHATS
      // ========================================
      chats: [],
      chatsLastFetch: null,

      // Focused contact: set when user clicks "Ir al contacto" in notifications
      focusedContactPhone: null,
      setFocusedContactPhone: (phone) => set({ focusedContactPhone: phone }),

      // Pending drill-down: cross-component navigation signal (e.g. heatmap -> todolist)
      // payload shape: { view: string, agent?: string, next_followup_date?: string, workArea?: string|null }
      pendingDrilldown: null,
      setPendingDrilldown: (payload) => set({ pendingDrilldown: payload }),
      clearPendingDrilldown: () => set({ pendingDrilldown: null }),


      setChats: (chats) => {
        set({
          chats,
          chatsLastFetch: Date.now()
        })
      },

      updateChat: (phone, updates) => {
        set((state) => ({
          chats: state.chats.map(chat =>
            chat.phone === phone ? { ...chat, ...updates } : chat
          )
        }))
      },

      addChat: (chat) => {
        set((state) => ({
          chats: [chat, ...state.chats]
        }))
      },

      // ========================================
      // MESSAGES
      // ========================================
      messages: {}, // { [phone]: [messages] }
      messagesLastFetch: {}, // { [phone]: timestamp }

      setMessages: (phone, messages) => {
        set((state) => ({
          messages: {
            ...state.messages,
            [phone]: messages
          },
          messagesLastFetch: {
            ...state.messagesLastFetch,
            [phone]: Date.now()
          }
        }))
      },

      addMessage: (phone, message) => {
        set((state) => ({
          messages: {
            ...state.messages,
            [phone]: [...(state.messages[phone] || []), message]
          }
        }))
      },

      updateMessage: (phone, messageId, updates) => {
        set((state) => ({
          messages: {
            ...state.messages,
            [phone]: state.messages[phone]?.map(msg =>
              msg.id === messageId ? { ...msg, ...updates } : msg
            ) || []
          }
        }))
      },

      // ========================================
      // PROSPECTS
      // ========================================
      prospects: {}, // { [phone]: prospect_data }
      prospectsLastFetch: {}, // { [phone]: timestamp }

      setProspect: (phone, data) => {
        set((state) => ({
          prospects: {
            ...state.prospects,
            [phone]: data
          },
          prospectsLastFetch: {
            ...state.prospectsLastFetch,
            [phone]: Date.now()
          }
        }))
      },

      updateProspect: (phone, updates) => {
        set((state) => ({
          prospects: {
            ...state.prospects,
            [phone]: {
              ...state.prospects[phone],
              ...updates
            }
          }
        }))
      },

      // ========================================
      // FILTERS
      // ========================================
      activeFilters: {
        search: '',
        stage: null,
        agent: null,
        label: null,      // { id, name, color }
        department: null, // { id, name }
        sortOrder: 'recent', // 'recent' | 'oldest' | 'name-asc' | 'name-desc'
      },

      setFilters: (filters) => {
        set({ activeFilters: filters })
      },

      setFilter: (key, value) => {
        set((state) => ({
          activeFilters: {
            ...state.activeFilters,
            [key]: value
          }
        }))
      },

      clearFilters: () => {
        set({
          activeFilters: {
            search: '',
            stage: null,
            agent: null,
            label: null,
            department: null,
            sortOrder: 'recent',
          }
        })
      },

      // ========================================
      // UI STATE
      // ========================================
      selectedChatPhone: null,
      isFilterPanelCollapsed: false,
      isProspectPanelCollapsed: false,

      setSelectedChat: (phone) => {
        set({ selectedChatPhone: phone })
      },

      toggleFilterPanel: () => {
        set((state) => ({
          isFilterPanelCollapsed: !state.isFilterPanelCollapsed
        }))
      },

      toggleProspectPanel: () => {
        set((state) => ({
          isProspectPanelCollapsed: !state.isProspectPanelCollapsed
        }))
      },

      // ========================================
      // CALL TRACKING
      // ========================================
      activeCalls: [], // Array de { id, contactoId, contactPhone, contactName, startedAt, agentPhone, agentName }
      pendingNoteCalls: [], // Llamadas terminadas esperando nota del usuario
      callsLoaded: false,

      setActiveCalls: (calls) => {
        set({
          activeCalls: calls.map(call => ({
            id: call.id,
            contactoId: call.contacto_id,
            contactPhone: call.contact_phone,
            contactName: call.contact_name,
            startedAt: call.started_at,
            agentPhone: call.agent_phone,
            agentName: call.agent_name
          })),
          callsLoaded: true
        })
      },

      addActiveCall: (call) => {
        set((state) => ({
          activeCalls: [...state.activeCalls, call]
        }))
      },

      removeActiveCall: (callId) => {
        set((state) => ({
          activeCalls: state.activeCalls.filter(c => c.id !== callId)
        }))
      },

      // Mover llamada de activa a pendiente de nota
      moveCallToPendingNote: (callId) => {
        const call = get().activeCalls.find(c => c.id === callId)
        if (!call) return null

        // Calcular duración
        const start = new Date(call.startedAt)
        const now = new Date()
        const durationSeconds = Math.floor((now - start) / 1000)

        const pendingCall = {
          ...call,
          endedAt: now.toISOString(),
          durationSeconds
        }

        set((state) => ({
          activeCalls: state.activeCalls.filter(c => c.id !== callId),
          pendingNoteCalls: [...state.pendingNoteCalls, pendingCall]
        }))

        return pendingCall
      },

      // Remover llamada de pendientes (después de guardar o descartar)
      removePendingNoteCall: (callId) => {
        set((state) => ({
          pendingNoteCalls: state.pendingNoteCalls.filter(c => c.id !== callId)
        }))
      },

      // Obtener llamada pendiente de nota
      getPendingNoteCall: (callId) => {
        return get().pendingNoteCalls.find(c => c.id === callId)
      },

      isInCall: (contactoId) => {
        return get().activeCalls.some(c => c.contactoId === contactoId)
      },

      getCallForContact: (contactoId) => {
        return get().activeCalls.find(c => c.contactoId === contactoId)
      },

      // ========================================
      // CACHE HELPERS
      // ========================================
      shouldFetchChats: () => {
        const { chatsLastFetch } = get()
        const CHAT_CACHE_TTL = 30 * 1000 // 30 segundos
        return !chatsLastFetch || (Date.now() - chatsLastFetch > CHAT_CACHE_TTL)
      },

      shouldFetchMessages: (phone) => {
        const { messagesLastFetch } = get()
        const MESSAGE_CACHE_TTL = 60 * 1000 // 60 segundos
        const lastFetch = messagesLastFetch[phone]
        return !lastFetch || (Date.now() - lastFetch > MESSAGE_CACHE_TTL)
      },

      shouldFetchProspect: (phone) => {
        const { prospectsLastFetch } = get()
        const PROSPECT_CACHE_TTL = 5 * 60 * 1000 // 5 minutos
        const lastFetch = prospectsLastFetch[phone]
        return !lastFetch || (Date.now() - lastFetch > PROSPECT_CACHE_TTL)
      },

      // === Quotations state ===
      quotationsList: [],
      quotationsListFetch: null,
      quotationEditing: null,

      setQuotationsList: (list) => set({ quotationsList: list, quotationsListFetch: Date.now() }),
      setQuotationEditing: (q) => set({ quotationEditing: q }),
      clearQuotationEditing: () => set({ quotationEditing: null }),
      shouldFetchQuotationsList: () => {
        const { quotationsListFetch } = get();
        if (!quotationsListFetch) return true;
        return Date.now() - quotationsListFetch > 60000;
      },

      // ========================================
      // FROZEN MODE (estabilidad de orden grilla TodoList)
      // ========================================
      // Snapshot del orden congelado mientras el agente trabaja. Updates in-place;
      // nuevos arriba; zombies con strikethrough. Triggers descongele: cambio
      // orden/area/folder/label/dept, reset, click chip, o 3min sin modificación
      // persistida del agente.
      frozen: freshFrozenState(),

      enterFrozen: (contacts) => {
        const phones = contacts.map(c => c.phone).filter(Boolean)
        set((state) => ({
          frozen: {
            active: true,
            snapshot: phones,
            // Si fullDayMode esta activo, el deadline se extiende hasta el proximo
            // 00:00 Chile. Caso contrario, +3min como antes.
            deadline: state.frozen.fullDayMode
              ? computeNextMidnightChile()
              : Date.now() + FROZEN_TTL_MS,
            crossAgentDots: {},
            newRowsHighlight: {},
            zombies: {},
            fullDayMode: state.frozen.fullDayMode, // preservar la preferencia
          }
        }))
      },

      // Toggle del opt-in fullDayMode (per-agente, persistido).
      // Si la grilla ya esta frozen activa, recalcula el deadline al modo apropiado.
      setFullDayMode: (enabled) => {
        set((state) => {
          if (state.frozen.fullDayMode === enabled) return state
          return {
            frozen: {
              ...state.frozen,
              fullDayMode: enabled,
              deadline: state.frozen.active
                ? (enabled ? computeNextMidnightChile() : Date.now() + FROZEN_TTL_MS)
                : state.frozen.deadline
            }
          }
        })
      },

      // Reemplaza el snapshot al nuevo set de phones SIN tocar el deadline.
      // Llamado cuando el agente cambia search/sort/filtros mientras frozen
      // sigue activo: el set de phones cambia naturalmente, y los indicadores
      // (zombies/newRows) del query anterior ya no aplican. Mantiene los dots
      // (cross-agent) cuyos phones siguen presentes — son acuses pendientes
      // del agente que persisten cross-query.
      refreshSnapshot: (contacts) => {
        const phones = contacts.map(c => c?.phone).filter(Boolean)
        set((state) => {
          if (!state.frozen.active) return state
          const phoneSet = new Set(phones)
          const cleanDots = {}
          for (const [p, v] of Object.entries(state.frozen.crossAgentDots)) {
            if (phoneSet.has(p)) cleanDots[p] = v
          }
          return {
            frozen: {
              ...state.frozen,
              snapshot: phones,
              zombies: {},
              newRowsHighlight: {},
              crossAgentDots: cleanDots,
            }
          }
        })
      },

      resetDeadline: () => {
        set((state) => {
          if (!state.frozen.active) return state
          // Respetar fullDayMode: si esta activo, el deadline siempre se extiende a 00:00 Chile.
          const nextDeadline = state.frozen.fullDayMode
            ? computeNextMidnightChile()
            : Date.now() + FROZEN_TTL_MS
          return { frozen: { ...state.frozen, deadline: nextDeadline } }
        })
      },

      // ensureFrozen (INC-036, 2026-05-04): garantiza que frozen este activo tras una
      // mutacion del agente. Si frozen ya esta activo: extiende deadline (igual que
      // resetDeadline). Si esta expirado/inactivo: re-activa con snapshot del state
      // actual de contacts.
      //
      // Antes, los handlers de mutacion solo llamaban resetDeadline() que es no-op
      // cuando frozen.active=false. Resultado: si el agente editaba un prospecto
      // despues de que el frozen expiraba, el cambio se aplicaba al backend y al
      // siguiente refresh la grilla se reordenaba (perdiendo la posicion). Con
      // ensureFrozen, cualquier mutacion del agente reactiva el congelamiento del
      // orden actual durante 3min mas (o hasta 00:00 Chile en fullDayMode).
      ensureFrozen: (contacts) => {
        set((state) => {
          if (state.frozen.active) {
            // Solo extender deadline (mantener snapshot existente).
            const nextDeadline = state.frozen.fullDayMode
              ? computeNextMidnightChile()
              : Date.now() + FROZEN_TTL_MS
            return { frozen: { ...state.frozen, deadline: nextDeadline } }
          }
          // Frozen inactivo: re-activar con snapshot del state actual.
          const phones = (contacts || []).map(c => c?.phone).filter(Boolean)
          if (phones.length === 0) return state
          const nextDeadline = state.frozen.fullDayMode
            ? computeNextMidnightChile()
            : Date.now() + FROZEN_TTL_MS
          return {
            frozen: {
              ...freshFrozenState(),
              active: true,
              snapshot: phones,
              deadline: nextDeadline,
              fullDayMode: state.frozen.fullDayMode, // preservar preferencia
            }
          }
        })
      },

      markCrossAgentDot: (phone) => {
        if (!phone) return
        set((state) => ({
          frozen: {
            ...state.frozen,
            crossAgentDots: { ...state.frozen.crossAgentDots, [phone]: true }
          }
        }))
      },

      clearDot: (phone) => {
        set((state) => {
          if (!state.frozen.crossAgentDots[phone]) return state
          const next = { ...state.frozen.crossAgentDots }
          delete next[phone]
          return { frozen: { ...state.frozen, crossAgentDots: next } }
        })
      },

      markZombie: (phone, reason, target) => {
        if (!phone) return
        set((state) => ({
          frozen: {
            ...state.frozen,
            zombies: { ...state.frozen.zombies, [phone]: { reason, target } }
          }
        }))
      },

      clearZombie: (phone) => {
        set((state) => {
          if (!state.frozen.zombies[phone]) return state
          const next = { ...state.frozen.zombies }
          delete next[phone]
          // Tambien sacar del snapshot
          const newSnapshot = state.frozen.snapshot.filter(p => p !== phone)
          return { frozen: { ...state.frozen, zombies: next, snapshot: newSnapshot } }
        })
      },

      markNewRow: (phone) => {
        if (!phone) return
        set((state) => {
          // Agregar a snapshot[0] si no esta
          const inSnapshot = state.frozen.snapshot.includes(phone)
          const newSnapshot = inSnapshot
            ? state.frozen.snapshot
            : [phone, ...state.frozen.snapshot]
          return {
            frozen: {
              ...state.frozen,
              snapshot: newSnapshot,
              newRowsHighlight: { ...state.frozen.newRowsHighlight, [phone]: Date.now() }
            }
          }
        })
      },

      clearNewRowHighlight: (phone) => {
        set((state) => {
          if (!state.frozen.newRowsHighlight[phone]) return state
          const next = { ...state.frozen.newRowsHighlight }
          delete next[phone]
          return { frozen: { ...state.frozen, newRowsHighlight: next } }
        })
      },

      extendFrozenSnapshot: (contacts) => {
        const phones = contacts.map(c => c?.phone).filter(Boolean)
        if (phones.length === 0) return
        set((state) => {
          if (!state.frozen.active) return state
          // Append phones que no estén ya en snapshot (preservar orden de inserción)
          const existing = new Set(state.frozen.snapshot)
          const additions = phones.filter(p => !existing.has(p))
          if (additions.length === 0) return state
          return {
            frozen: {
              ...state.frozen,
              snapshot: [...state.frozen.snapshot, ...additions]
            }
          }
        })
      },

      exitFrozen: () => {
        // Preservar la preferencia fullDayMode al descongelar (es config del agente).
        // pendingNew se limpia: al descongelar, los nuevos se inyectaran via loadContacts.
        set((state) => ({
          frozen: {
            ...freshFrozenState(),
            fullDayMode: state.frozen.fullDayMode,
          },
          pendingNew: [],
        }))
      },

      // PENDING NEW (contactos nuevos detectados durante auto-refresh, pendientes de
      // mostrar en grilla). Independiente del frozen TTL: persiste hasta que el agente
      // clickea el banner pill (PendingNewPill) o hasta que se descongela el orden.
      // Slice nuevo 2026-05-04 (INC-034): reemplaza el auto-prepend de useFrozenMerge
      // que provocaba drift de scroll por compensacion preemptiva en useScrollAnchor.
      pendingNew: [],

      addPendingNewPhones: (phones) => set((state) => {
        if (!Array.isArray(phones) || phones.length === 0) return state
        const merged = new Set(state.pendingNew)
        for (const p of phones) {
          if (p) merged.add(p)
        }
        if (merged.size === state.pendingNew.length) return state
        return { pendingNew: Array.from(merged) }
      }),

      clearPendingNew: () => set((state) => {
        if (state.pendingNew.length === 0) return state
        return { pendingNew: [] }
      }),
    }),
    {
      name: 'crm-web-storage',
      partialize: (state) => ({
        // Solo persistir auth y UI preferences
        user: state.user,
        // [F2] token ya no se persiste (la sesion vive en la cookie httpOnly)
        isFilterPanelCollapsed: state.isFilterPanelCollapsed,
        isProspectPanelCollapsed: state.isProspectPanelCollapsed,
        // Persistir llamadas activas para recovery (se sincroniza con BD al montar)
        activeCalls: state.activeCalls,
        // Persistir el opt-in del modo dia completo del frozen mode (config del agente).
        // El resto del slice frozen NO se persiste (siempre fresh al recargar).
        _frozenFullDayMode: state.frozen?.fullDayMode === true,
        // NO persistir chats/messages/prospects (siempre fresh)
      }),
      // Merge custom: aplicamos el flag persistido al slice frozen al rehidratar.
      merge: (persisted, current) => {
        const merged = { ...current, ...persisted }
        // Reconstruir frozen preservando el resto del estado fresco.
        merged.frozen = {
          ...current.frozen,
          fullDayMode: persisted?._frozenFullDayMode === true,
        }
        // Limpiar la propiedad helper que no pertenece al estado runtime.
        delete merged._frozenFullDayMode
        return merged
      },
    }
  )
)

export default useCRMStore
